import React, { useState } from "react";
import api from "@/services/api.js";
import toast from "react-hot-toast";
import { Search, UserPlus, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const AddFriend = ({ onRequestSent }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [foundUser, setFoundUser] = useState(null);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSearch = async () => {
    if (!username.trim()) {
      return;
    }
    setSearching(true);
    setFoundUser(null);
    try {
      // const response = await api.get(`/users/username/${username}`);
      const response = await api.get(`/users/username/${encodeURIComponent(username)}`);
      setFoundUser(response.data);
    } catch {
      toast.error(t("addFriend_searchError"));
    } finally {
      setSearching(false);
    }
  };

  const handleSendRequest = async () => {
    if (!foundUser) return;
    setSending(true);
    try {
      await api.post(`/friendships/request/${foundUser.id}`);
      toast.success(t("addFriend_requestSuccess", { username: foundUser.username }));
      setFoundUser(null);
      setUsername("");
      onRequestSent();
    } catch (err) {
      const message = err.response?.data?.message || t("addFriend_requestError");
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder={t("addFriend_searchPlaceholder")}
          className="flex-1 px-4 py-2 rounded-xl bg-zinc-800/60 border border-white/8 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/50 transition-colors"
        />
        <button
          onClick={handleSearch}
          disabled={searching || !username.trim()}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-500/10 border border-violet-500/20 text-violet-400 text-sm hover:bg-violet-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {searching
            ? <Loader2 size={15} className="animate-spin" />
            : <Search size={15} />
          }
          {t("addFriend_searchButton")}
        </button>
      </div>

      {/* Found user preview */}
      {foundUser && (
        <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-white/8 bg-white/2">
          <div className="flex items-center gap-3">
            <div className="relative">
              {foundUser.avatarPath ? (
                <img
                  src={foundUser.avatarPath}
                  alt={foundUser.username}
                  className="w-9 h-9 rounded-full object-cover"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-zinc-700 flex items-center justify-center text-zinc-300 text-sm font-medium">
                  {foundUser.username?.charAt(0).toUpperCase()}
                </div>
              )}
              <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-zinc-900
                ${foundUser.online ? "bg-green-500" : "bg-zinc-600"}`}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-sm text-white font-medium">{foundUser.username}</span>
              <span className={`text-xs ${foundUser.online ? "text-green-400" : "text-zinc-500"}`}>
                {foundUser.online ? t("addFriend_online") : t("addFriend_offline")}
              </span>
            </div>
          </div>

          <button
            onClick={handleSendRequest}
            disabled={sending}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-violet-400 hover:bg-violet-400/10 border border-transparent hover:border-violet-400/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending
              ? <Loader2 size={14} className="animate-spin" />
              : <UserPlus size={14} />
            }
            {t("addFriend_addButton")}
          </button>
        </div>
      )}
    </div>
  );
};

export default AddFriend;